import { type ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { Download, Maximize2, ZoomIn, ZoomOut } from 'lucide-react'

import { AnnotateMode } from './annotate/AnnotateMode'
import { AssignerToggles } from './components/AssignerToggles'
import AttributionStages from './components/AttributionStages'
import { ChunkControls } from './components/ChunkControls'
import { buildSpeakerStyleMap, extendSpeakerStyleMap } from './components/colors'
import JobsPanel from './components/JobsPanel'
import Legend from './components/Legend'
import { liveOverlaps } from './components/liveOverlaps'
import ModeBar from './components/ModeBar'
import StatBand from './components/StatBand'
import Swimlane, {
  clampViewport,
  formatMs,
  type LaneSpec,
  MIN_TIMELINE_VIEW_MS,
  type ViewportChange,
  zoomViewport,
} from './components/Swimlane'
import TranscriptColumns, { TranscriptColumn } from './components/TranscriptColumns'
import { ReplayMode } from './replay/ReplayMode'
import {
  applySpeakerMappings,
  artifactEndMs,
  assignerOnlyRows,
  deriveSpeakerMappings,
  deriveUnassignedPeople,
  entriesToRows,
  observationsToRows,
  parseChorusTranscript,
  parseObservationNdjson,
  parseRawTranscript,
  parseTranscriptNdjson,
  reconcileNamedSpeakerColors,
  rowsToSpans,
  transcriptStats,
} from './artifactDerivations'
import { liveArtifactQueryOptions, refinedArtifactQueryOptions } from './artifactQueries'
import { parseAttributionDiagnostics, withAttributionMethods } from './attributionMethods'
import { selectAttributionJob } from './jobSelection'
import { liveProviderLabel } from './liveProvider'
import { compareHref, parseViewerMode, type ViewerMode } from './mode'
import { attributionViewerKeys } from './queryKeys'
import { finalLaneLabel, PRIOR_LANE_LABEL, relabelerSpans, shippedRecordRows } from './relabelerLanes'
import { adjacentSourceChunkIndex, deriveSourceChunks, type SourceChunk, sourceChunkIndexAtTime } from './sourceChunks'
import { useAudioClipPlayer } from './useAudioClipPlayer'
import { useShippedRecord } from './useShippedRecord'
import { suppressViewerShortcut } from './viewerShortcuts'

import styles from './AttributionViewerPage.module.scss'
import baseStyles from '@/pages/base.module.scss'

import { isApiError } from '@/api'
import {
  getMeetingAttributionAudioDownloadUrl,
  getMeetingAttributionAudioUrl,
  getMeetingAttributionJobs,
  getMeetingAttributionObservations,
  getMeetingAttributionProviders,
  getMeetingAttributionSpeakerMap,
  rerunMeetingAttributionJob,
  type ShippedAttributionStatus,
} from '@/api/internal'
import Button from '@/components/Button'
import { useToast } from '@/components/Toast/ToastContext'
import { selectIsInternalAdmin } from '@/store/features/user/userSlice'
import { downloadBlob } from '@/utils/csv'

type ArtifactState = 'loading' | 'present' | 'absent' | 'error'

type QueryState = {
  data: unknown
  error: unknown
  isError: boolean
  isPending: boolean
}

const artifactState = (query: QueryState): ArtifactState => {
  if (query.isError) return isApiError(query.error) && query.error.status === 404 ? 'absent' : 'error'
  if (query.data !== undefined) return 'present'
  if (query.isPending) return 'loading'
  if (isApiError(query.error) && query.error.status === 404) return 'absent'
  return 'error'
}

const shouldRetryArtifact = (failureCount: number, error: unknown): boolean =>
  failureCount < 1 && (!isApiError(error) || error.status >= 500)

const artifactFilename = (meetingId: string, stem: string): string => `${meetingId.replace(/[^\w.-]+/g, '_')}-${stem}`

export const isInteractiveEntry = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return (
    target.isContentEditable ||
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.closest('button, a, [role="button"], [role="slider"]') != null
  )
}

const downloadUrl = (url: string) => {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const AttributionViewer = ({
  meetingId,
  effectiveJobId,
  isHistoricalJob,
}: {
  meetingId: string
  effectiveJobId: string | null
  isHistoricalJob: boolean
}) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const [viewport, setViewportState] = useState<{
    from: number
    to: number
  } | null>(null)
  const [hoverTime, setHoverTime] = useState<number | null>(null)
  const [pinnedTime, setPinnedTime] = useState<number | null>(null)
  const [selectionTime, setSelectionTime] = useState<number | null>(null)
  const [playOnSkip, setPlayOnSkip] = useState(true)
  const [previewMs, setPreviewMs] = useState<number | null>(5000)
  const [expandedProviderScope, setExpandedProviderScope] = useState<string | null>(null)
  const [showFinal, setShowFinal] = useState(true)
  const [showPrior, setShowPrior] = useState(false)
  const [refreshingArtifacts, setRefreshingArtifacts] = useState(false)
  const refreshGeneration = useRef(0)
  const pendingClipRef = useRef<SourceChunk | null>(null)
  const [audioRequested, setAudioRequested] = useState(false)
  const [audioReady, setAudioReady] = useState(false)
  const [audioDurationMs, setAudioDurationMs] = useState<number | null>(null)
  const [audioMediaFailed, setAudioMediaFailed] = useState(false)
  const [audioLoadAttempt, setAudioLoadAttempt] = useState(0)
  const audioRetryRef = useRef(0)
  const {
    audioRef,
    audioTime,
    isPlaying,
    pause,
    playbackRate,
    playFrom,
    seekTo,
    setPlaybackRate,
    togglePlayback,
    onTimeUpdate,
    onSeeked,
    onPlay,
    onPause,
    onEnded,
    onStopped,
  } = useAudioClipPlayer()

  const queryDefaults = {
    enabled: Boolean(meetingId),
    gcTime: 0,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: shouldRetryArtifact,
    staleTime: Infinity,
  } as const

  const liveQuery = useQuery({
    ...queryDefaults,
    ...liveArtifactQueryOptions(meetingId),
  })
  const refinedQuery = useQuery({
    ...queryDefaults,
    ...refinedArtifactQueryOptions(meetingId, effectiveJobId),
  })
  const refinedBody = refinedQuery.isError ? undefined : refinedQuery.data?.data
  const refinedRevision = refinedQuery.data?.attributionRevision ?? null
  const providerOptions = (revision: string | null) => ({
    ...queryDefaults,
    queryKey: attributionViewerKeys.providers(meetingId, revision),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const response = await getMeetingAttributionProviders(meetingId, revision, signal)
      if ((response.data.displayed_revision ?? null) !== revision)
        throw {
          status: 409,
          message: 'The refined transcript changed. Refresh artifacts to compare this revision.',
        }
      return response.data
    },
  })
  const providersQuery = useQuery({
    ...providerOptions(refinedRevision),
    enabled:
      !isHistoricalJob &&
      Boolean(meetingId) &&
      refinedQuery.isSuccess &&
      !refinedQuery.isFetching &&
      !refreshingArtifacts,
  })
  const providersReady =
    !isHistoricalJob &&
    refinedQuery.isSuccess &&
    !refinedQuery.isFetching &&
    !refreshingArtifacts &&
    providersQuery.isSuccess
  const providerData = providersReady ? providersQuery.data : undefined
  const soniox = providerData?.providers.find((provider) => provider.provider === 'soniox')
  const chorus = providerData?.providers.find((provider) => provider.provider === 'chorus')
  const rawBody = soniox?.status === 'available' ? (soniox.raw_json ?? undefined) : undefined
  const chorusBody = chorus?.status === 'available' ? (chorus.raw_json ?? undefined) : undefined
  const observationsQuery = useQuery({
    ...queryDefaults,
    queryKey: attributionViewerKeys.artifact(meetingId, 'observations'),
    queryFn: ({ signal }) => getMeetingAttributionObservations(meetingId, signal).then((response) => response.data),
  })
  const speakerMapQuery = useQuery({
    ...queryDefaults,
    queryKey: attributionViewerKeys.speakerMap(meetingId, effectiveJobId),
    queryFn: ({ signal }) =>
      getMeetingAttributionSpeakerMap(meetingId, signal, effectiveJobId).then((response) => response.data),
  })
  const audioQuery = useQuery({
    ...queryDefaults,
    enabled: Boolean(meetingId) && audioRequested,
    queryKey: attributionViewerKeys.audio(meetingId, effectiveJobId),
    queryFn: ({ signal }) =>
      getMeetingAttributionAudioUrl(meetingId, signal, effectiveJobId).then((response) => response.data),
  })
  const audioDownloadMutation = useMutation({
    mutationFn: async () => {
      const response = await getMeetingAttributionAudioDownloadUrl(meetingId, undefined, effectiveJobId)
      if (!response.data.url) throw new Error('Audio download URL was unavailable')
      return response.data.url
    },
    onSuccess: downloadUrl,
    onError: () => {
      updateToast('Audio could not be downloaded. Please try again.', 'error')
    },
  })

  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const refinedLabel = finalLaneLabel(providerData?.served_provider)
  const refetchLive = liveQuery.refetch
  const refetchObservations = observationsQuery.refetch
  const refetchRaw = providersQuery.refetch
  const refetchRefined = refinedQuery.refetch

  useEffect(
    () => () => {
      refreshGeneration.current += 1
      void Promise.all([
        queryClient.cancelQueries({
          queryKey: attributionViewerKeys.artifactScope(meetingId),
        }),
        queryClient.cancelQueries({
          queryKey: attributionViewerKeys.speakerMapScope(meetingId),
        }),
        queryClient.cancelQueries({
          queryKey: attributionViewerKeys.relabeler(meetingId),
        }),
        queryClient.cancelQueries({
          queryKey: attributionViewerKeys.audioScope(meetingId),
        }),
      ]).then(() => {
        queryClient.removeQueries({
          queryKey: attributionViewerKeys.artifactScope(meetingId),
        })
        queryClient.removeQueries({
          queryKey: attributionViewerKeys.speakerMapScope(meetingId),
        })
        queryClient.removeQueries({
          queryKey: attributionViewerKeys.relabeler(meetingId),
        })
        // The compare page shares the audio scope and may already be observing it; only drop orphaned entries.
        const cache = queryClient.getQueryCache()
        for (const query of cache.findAll({ queryKey: attributionViewerKeys.audioScope(meetingId) }))
          if (query.getObserversCount() === 0) cache.remove(query)
      })
    },
    [meetingId, queryClient]
  )

  const liveArtifact = useMemo(() => parseTranscriptNdjson(liveQuery.data ?? ''), [liveQuery.data])
  const refinedArtifact = useMemo(() => parseTranscriptNdjson(refinedBody ?? ''), [refinedBody])
  const observationsArtifact = useMemo(
    () => parseObservationNdjson(observationsQuery.data ?? ''),
    [observationsQuery.data]
  )
  const rawArtifact = useMemo(() => parseRawTranscript(rawBody ?? ''), [rawBody])
  const chorusArtifact = useMemo(() => parseChorusTranscript(chorusBody ?? ''), [chorusBody])
  const speakerMappings = useMemo(
    () => deriveSpeakerMappings(speakerMapQuery.data?.speaker_map),
    [speakerMapQuery.data?.speaker_map]
  )
  const attributionDiagnostics = useMemo(
    () => (speakerMapQuery.isError ? null : parseAttributionDiagnostics(speakerMapQuery.data, refinedRevision)),
    [speakerMapQuery.data, speakerMapQuery.isError, refinedRevision]
  )
  const unassignedPeople = useMemo(
    () => deriveUnassignedPeople(speakerMapQuery.data?.speaker_map, speakerMappings),
    [speakerMapQuery.data?.speaker_map, speakerMappings]
  )
  const refinedRows = useMemo(
    () =>
      withAttributionMethods(
        applySpeakerMappings(entriesToRows(refinedArtifact.entries, 'refined'), speakerMappings),
        speakerMapQuery.data?.speaker_map,
        attributionDiagnostics
      ),
    [refinedArtifact.entries, speakerMappings, speakerMapQuery.data?.speaker_map, attributionDiagnostics]
  )
  const assignerRows = useMemo(
    () =>
      withAttributionMethods(
        assignerOnlyRows(refinedRows, speakerMapQuery.data?.speaker_map, speakerMappings),
        speakerMapQuery.data?.speaker_map,
        attributionDiagnostics,
        true
      ),
    [refinedRows, speakerMapQuery.data?.speaker_map, speakerMappings, attributionDiagnostics]
  )
  const liveLabel = useMemo(() => liveProviderLabel(liveArtifact.entries, 'Live clusters'), [liveArtifact.entries])
  const liveRows = useMemo(() => entriesToRows(liveArtifact.entries, 'live'), [liveArtifact.entries])
  const observationRows = useMemo(
    () =>
      reconcileNamedSpeakerColors(
        applySpeakerMappings(observationsToRows(observationsArtifact), speakerMappings),
        refinedRows
      ),
    [observationsArtifact, refinedRows, speakerMappings]
  )
  const rawRows = rawArtifact.rows
  const chorusRows = chorusArtifact.rows
  const chunks = useMemo(
    () =>
      deriveSourceChunks({
        liveRows,
        refinedRows: entriesToRows(refinedArtifact.entries, 'refined'),
        rawRows,
        chorusRows,
        observations: observationsArtifact,
      }),
    [liveRows, refinedArtifact.entries, rawRows, chorusRows, observationsArtifact]
  )
  const chunkIndex = selectionTime == null ? (chunks.length ? 0 : -1) : sourceChunkIndexAtTime(chunks, selectionTime)
  const selectedChunk = chunks[chunkIndex]
  const liveState =
    liveQuery.data?.trim() && liveArtifact.entries.length === 0 && liveArtifact.invalidLineCount > 0
      ? 'error'
      : artifactState(liveQuery)
  const refinedState =
    refinedBody?.trim() && refinedArtifact.entries.length === 0 && refinedArtifact.invalidLineCount > 0
      ? 'error'
      : artifactState(refinedQuery)
  const providerState = (body: string | undefined, usable: boolean): ArtifactState => {
    if (refinedQuery.isError) return artifactState(refinedQuery)
    if (refinedQuery.isPending || refinedQuery.isFetching || refreshingArtifacts) return 'loading'
    if (providersQuery.isError) return artifactState(providersQuery)
    if (providersQuery.isPending) return 'loading'
    return body === undefined ? 'absent' : usable ? 'present' : 'error'
  }
  const rawState = isHistoricalJob ? 'absent' : providerState(rawBody, rawArtifact.usable)
  const chorusState = isHistoricalJob ? 'absent' : providerState(chorusBody, chorusArtifact.usable)
  const observationsState =
    observationsQuery.data !== undefined && !observationsArtifact.usable ? 'error' : artifactState(observationsQuery)
  const speakerMapState =
    speakerMapQuery.data !== undefined && speakerMapQuery.data.speaker_map == null
      ? 'absent'
      : artifactState(speakerMapQuery)

  const hasUserLabels = useMemo(
    () =>
      speakerMappings.some(
        (mapping) =>
          mapping.origin === 'user' ||
          mapping.assignmentSources.some((source) => source.mechanism === 'user' && source.personId != null)
      ),
    [speakerMappings]
  )
  const priorVisible = showPrior && hasUserLabels && isInternalAdmin
  const shipped = useShippedRecord({
    meetingId,
    revision: refinedRevision,
    enabled: priorVisible && !isHistoricalJob && !refinedQuery.isPending,
  })
  const shippedRecord = shipped.isError ? undefined : shipped.data
  const priorRows = useMemo(
    () => (shippedRecord && priorVisible ? shippedRecordRows(shippedRecord, refinedRows) : []),
    [shippedRecord, priorVisible, refinedRows]
  )
  const sourceLanes = useMemo<LaneSpec[]>(
    () => [
      {
        id: 'live',
        label: liveLabel,
        state: liveState,
        spans: rowsToSpans(liveRows),
      },
      {
        id: 'observations',
        label: 'AX tree',
        state: observationsState,
        spans: rowsToSpans(observationRows),
      },
    ],
    [liveLabel, liveRows, liveState, observationRows, observationsState]
  )
  const providerLanes = useMemo<LaneSpec[]>(
    () => [
      {
        id: 'raw',
        label: 'Soniox clusters',
        state: rawState,
        spans: rowsToSpans(rawRows),
      },
      {
        id: 'chorus',
        label: 'Chorus clusters',
        state: chorusState,
        spans: rowsToSpans(chorusRows),
        subtracks: chorusRows.length
          ? ['mic', 'system']
              .map((channel) => ({
                label: channel === 'mic' ? 'Mic' : 'System',
                spans: rowsToSpans(chorusRows.filter((row) => row.channel === channel)),
              }))
              .filter((track) => track.spans.length > 0)
          : undefined,
      },
    ],
    [rawRows, rawState, chorusRows, chorusState]
  )
  const baseLanes = useMemo(() => [...sourceLanes, ...providerLanes], [sourceLanes, providerLanes])
  const servedProvider = providerData?.served_provider
  const activeProviderId =
    servedProvider && servedProvider !== 'openai' ? { soniox: 'raw', chorus: 'chorus' }[servedProvider] : null
  const providerScope = `${meetingId}:${refinedRevision}:${servedProvider}`
  const areProvidersExpanded = expandedProviderScope === providerScope
  const providerStatus = providerState(undefined, false)
  const visibleProviderLanes = useMemo<LaneSpec[]>(() => {
    if (isHistoricalJob) return []
    const activeProviderLane = providerLanes.find((lane) => lane.id === activeProviderId)
    const comparisonLanes = providerLanes.filter(
      (lane) => lane.id !== activeProviderId && lane.state === 'present' && lane.spans.length > 0
    )
    const providerStatusLane: LaneSpec = {
      id: 'providers',
      label: 'Provider clusters',
      state: providerStatus,
      spans: [],
      emptyMessage:
        servedProvider === 'openai' ? 'OpenAI clusters are not retained.' : 'The active provider is unknown.',
    }
    const providerLane = activeProviderLane ?? providerStatusLane
    return [
      {
        ...providerLane,
        disclosure: comparisonLanes.length
          ? {
              isExpanded: areProvidersExpanded,
              label: `${areProvidersExpanded ? 'Hide' : 'Show'} comparison providers`,
              controls: comparisonLanes.map((lane) => `provider-lane-${lane.id}`).join(' '),
              onToggle: () => setExpandedProviderScope(areProvidersExpanded ? null : providerScope),
            }
          : undefined,
      },
      ...(areProvidersExpanded
        ? comparisonLanes.map((lane) => ({
            ...lane,
            elementId: `provider-lane-${lane.id}`,
            note: activeProviderLane ? 'Shadow' : 'Comparison',
          }))
        : []),
    ]
  }, [
    providerLanes,
    isHistoricalJob,
    activeProviderId,
    providerStatus,
    servedProvider,
    areProvidersExpanded,
    providerScope,
  ])

  const assignerLane = useMemo<LaneSpec>(
    () => ({
      id: 'refined-assigner',
      label: 'Assigner',
      note: 'Current speaker map, without LLM or user names',
      state: speakerMapState,
      spans: relabelerSpans(assignerRows),
      emptyMessage: 'No speaker map for this meeting.',
      counterpartLabel: refinedLabel,
    }),
    [speakerMapState, assignerRows, refinedLabel]
  )
  const finalLanes = useMemo<LaneSpec[]>(() => {
    const finalLane: LaneSpec = {
      id: 'refined',
      label: refinedLabel,
      state: refinedState,
      spans: rowsToSpans(refinedRows),
    }
    // Non-admins have no lane toggles, so the shipped assigner stays reachable as an expandable child.
    if (!isInternalAdmin) return [{ ...finalLane, child: assignerLane }]
    return showFinal ? [finalLane] : []
  }, [showFinal, isInternalAdmin, refinedLabel, refinedState, refinedRows, assignerLane])
  const priorLane = useMemo<LaneSpec[]>(() => {
    if (!priorVisible) return []
    const status = shippedRecord?.status
    const hasRows = status === 'complete' || status === 'summary_pending' || status === 'summary_missing'
    return [
      {
        id: 'prior',
        label: PRIOR_LANE_LABEL,
        note: (status && PRIOR_NOTES[status]) ?? PRIOR_NOTE,
        state: shipped.isError
          ? 'error'
          : isHistoricalJob
            ? 'absent'
            : shipped.isPending || status === 'revision_mismatch'
              ? 'loading'
              : hasRows
                ? 'present'
                : 'absent',
        spans: relabelerSpans(priorRows),
        emptyMessage: isHistoricalJob ? 'Only available for the displayed job.' : status && PRIOR_EMPTY[status],
        counterpartLabel: refinedLabel,
      },
    ]
  }, [priorVisible, shippedRecord, shipped.isError, shipped.isPending, isHistoricalJob, priorRows, refinedLabel])
  const lanes = useMemo(
    () => [...sourceLanes, ...visibleProviderLanes, ...finalLanes, ...priorLane],
    [sourceLanes, visibleProviderLanes, finalLanes, priorLane]
  )
  const endMs = useMemo(
    () =>
      artifactEndMs([
        { rows: liveRows },
        { rows: observationRows },
        { rows: rawRows },
        { rows: chorusRows },
        { rows: refinedRows },
        { rows: priorRows },
      ]),
    [liveRows, observationRows, rawRows, chorusRows, refinedRows, priorRows]
  )
  const speakerStyles = useMemo(() => {
    const identitiesOf = (specs: LaneSpec[]) =>
      specs.flatMap((lane) => lane.spans.flatMap((span) => (span.speaker ? [span.speaker] : [])))
    return extendSpeakerStyleMap(
      buildSpeakerStyleMap([
        ...identitiesOf(baseLanes),
        // Final stays registered while hidden so AX tree and mapping colours still reconcile against it.
        ...refinedRows.flatMap((row) => (row.speaker ? [row.speaker] : [])),
        ...speakerMappings.map((mapping) => ({
          key: `mapping:${mapping.speakerId}`,
          colorKey: mapping.colorKey,
          label: `Mapping · refined · ${mapping.name ?? `Speaker ${mapping.speakerId}`}`,
          name: mapping.name,
          speakerId: mapping.speakerId,
        })),
      ]),
      [...priorRows, ...(isInternalAdmin ? [] : assignerRows)].flatMap((row) => (row.speaker ? [row.speaker] : []))
    )
  }, [baseLanes, refinedRows, priorRows, assignerRows, speakerMappings, isInternalAdmin])

  const viewStart = Math.min(Math.max(0, viewport?.from ?? 0), Math.max(0, endMs - 1))
  const viewEnd = Math.min(endMs, Math.max(viewStart + 1, viewport?.to ?? endMs))
  const isZoomed = viewStart > 0 || viewEnd < endMs
  const playheadTime = (isPlaying ? audioTime : pinnedTime) ?? audioTime
  const inspectionTime = hoverTime ?? playheadTime
  const zoomCenter = inspectionTime ?? (viewStart + viewEnd) / 2
  const minimumView = Math.min(MIN_TIMELINE_VIEW_MS, endMs)

  const viewportFrameRef = useRef<number | null>(null)
  const pendingViewportRef = useRef<ViewportChange | null>(null)

  const cancelPendingViewport = useCallback(() => {
    if (viewportFrameRef.current != null) window.cancelAnimationFrame(viewportFrameRef.current)
    viewportFrameRef.current = null
    pendingViewportRef.current = null
  }, [])

  useEffect(() => cancelPendingViewport, [cancelPendingViewport])

  useEffect(() => {
    cancelPendingViewport()
    setViewportState(null)
  }, [cancelPendingViewport, meetingId])

  const writeViewport = useCallback(
    ({ from, to }: ViewportChange) => {
      if (from <= 0 && to >= endMs) {
        setViewportState(null)
      } else {
        setViewportState({ from: Math.round(from), to: Math.round(to) })
      }
    },
    [endMs]
  )

  const setViewport = useCallback(
    (change: ViewportChange) => {
      if (!change.replace) {
        cancelPendingViewport()
        writeViewport(change)
        return
      }
      pendingViewportRef.current = change
      if (viewportFrameRef.current != null) return
      viewportFrameRef.current = window.requestAnimationFrame(() => {
        viewportFrameRef.current = null
        const pending = pendingViewportRef.current
        pendingViewportRef.current = null
        if (pending) writeViewport(pending)
      })
    },
    [cancelPendingViewport, writeViewport]
  )

  const resetZoom = useCallback(() => {
    cancelPendingViewport()
    setViewportState(null)
  }, [cancelPendingViewport])

  const requestClip = useCallback(
    (range: SourceChunk) => {
      const clip = {
        startMs: range.startMs,
        endMs: previewMs == null ? range.endMs : Math.min(range.endMs, range.startMs + previewMs),
      }
      setAudioRequested(true)
      if (audioReady) playFrom(clip.startMs, clip.endMs)
      else pendingClipRef.current = clip
    },
    [audioReady, playFrom, previewMs]
  )

  const cueRange = useCallback(
    (range: SourceChunk, play = playOnSkip) => {
      pendingClipRef.current = null
      pause()
      setHoverTime(null)
      setSelectionTime(range.startMs)
      setPinnedTime(range.startMs)
      seekTo(range.startMs)
      if (range.startMs < viewStart || range.startMs >= viewEnd) {
        setViewport(clampViewport(range.startMs - (viewEnd - viewStart) / 4, viewEnd - viewStart, 0, endMs))
      }
      if (play) requestClip(range)
    },
    [endMs, pause, playOnSkip, requestClip, seekTo, setViewport, viewEnd, viewStart]
  )

  const cueTime = (time: number | null) => {
    pendingClipRef.current = null
    pause()
    setPinnedTime(time)
    setSelectionTime(time)
    if (time != null) seekTo(time)
  }

  const navigationTime = selectionTime ?? selectedChunk?.startMs ?? -1
  const previousChunk = adjacentSourceChunkIndex(chunks, navigationTime, -1)
  const nextChunk = adjacentSourceChunkIndex(chunks, navigationTime, 1)
  const navigateChunk = useCallback(
    (direction: -1 | 1) => {
      const index = adjacentSourceChunkIndex(chunks, navigationTime, direction)
      if (index >= 0) cueRange(chunks[index])
    },
    [chunks, cueRange, navigationTime]
  )

  const playSelection = useCallback(() => {
    if (isPlaying) {
      pause()
      return
    }
    if (!selectedChunk) return
    if (audioReady) {
      togglePlayback(
        selectedChunk.startMs,
        previewMs == null ? selectedChunk.endMs : Math.min(selectedChunk.endMs, selectedChunk.startMs + previewMs)
      )
    } else {
      requestClip(selectedChunk)
    }
  }, [audioReady, isPlaying, pause, previewMs, requestClip, selectedChunk, togglePlayback])

  const handleAudioPlay = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const time = event.currentTarget.currentTime * 1000
    if (time < viewStart || time > viewEnd) {
      const duration = viewEnd - viewStart
      setViewport(clampViewport(time - duration / 2, duration, 0, endMs))
    }
    onPlay(event)
  }

  const zoomAround = (center: number, scale: number) => {
    const ratio = Math.min(1, Math.max(0, (center - viewStart) / Math.max(1, viewEnd - viewStart)))
    setViewport(zoomViewport(viewStart, viewEnd, ratio, scale, 0, endMs))
  }

  const refreshArtifacts = async () => {
    const generation = ++refreshGeneration.current
    setRefreshingArtifacts(true)
    resetZoom()
    await queryClient.cancelQueries({
      queryKey: attributionViewerKeys.artifactScope(meetingId),
    })
    await queryClient.cancelQueries({
      queryKey: attributionViewerKeys.speakerMapScope(meetingId),
    })
    await queryClient.cancelQueries({
      queryKey: attributionViewerKeys.relabeler(meetingId),
    })
    queryClient.removeQueries({
      queryKey: [...attributionViewerKeys.artifactScope(meetingId), 'providers'],
    })
    queryClient.removeQueries({ queryKey: attributionViewerKeys.relabeler(meetingId) })
    void queryClient.invalidateQueries({ queryKey: attributionViewerKeys.jobs(meetingId) })
    try {
      const [refined] = await Promise.all([
        refinedQuery.refetch(),
        liveQuery.refetch(),
        observationsQuery.refetch(),
        speakerMapQuery.refetch(),
      ])
      if (!isHistoricalJob && refined.isSuccess && generation === refreshGeneration.current)
        await queryClient.fetchQuery(providerOptions(refined.data.attributionRevision ?? null))
    } catch {
      // The provider query retains its error for the lane's retry state.
    } finally {
      if (generation === refreshGeneration.current) setRefreshingArtifacts(false)
    }
  }

  const handleAudioError = () => {
    onStopped()
    setAudioReady(false)
    if (audioRetryRef.current > 0) {
      setAudioMediaFailed(true)
      return
    }
    audioRetryRef.current += 1
    void audioQuery.refetch().then((result) => {
      if (!result.data?.url) {
        setAudioMediaFailed(true)
        return
      }
      setAudioMediaFailed(false)
      setAudioLoadAttempt((attempt) => attempt + 1)
    })
  }

  const sourceQueries = isHistoricalJob
    ? [liveQuery, refinedQuery, observationsQuery, speakerMapQuery]
    : [liveQuery, refinedQuery, providersQuery, observationsQuery, speakerMapQuery]
  const refreshing = refreshingArtifacts || sourceQueries.some((query) => query.isFetching)
  const downloadableArtifacts = [
    { query: liveQuery, filename: 'live.ndjson' },
    { query: observationsQuery, filename: 'speaker-observations.ndjson' },
    {
      query: {
        ...providersQuery,
        data: rawBody,
        isPending: rawState === 'loading',
      },
      filename: 'soniox-raw.json',
    },
    {
      query: {
        ...providersQuery,
        data: chorusBody,
        isPending: chorusState === 'loading',
      },
      filename: 'chorus-raw.json',
    },
    {
      query: { ...refinedQuery, data: refinedBody },
      filename: 'refined.ndjson',
    },
  ].filter(({ filename }) => !isHistoricalJob || !['soniox-raw.json', 'chorus-raw.json'].includes(filename))
  const artifactDownloadError = downloadableArtifacts.some(
    ({ query }) => query.isError && !(isApiError(query.error) && query.error.status === 404)
  )
  const canDownloadAll =
    !artifactDownloadError &&
    !refreshing &&
    downloadableArtifacts.some(
      ({ query }) => !query.isError && typeof query.data === 'string' && query.data.length > 0
    ) &&
    downloadableArtifacts.every(({ query }) => !query.isFetching && !query.isPending)
  const downloadAllMutation = useMutation({
    mutationFn: async () => {
      const { default: JSZip } = await import('jszip')
      const archive = new JSZip()
      for (const { query, filename } of downloadableArtifacts) {
        if (!query.isError && typeof query.data === 'string') {
          archive.file(artifactFilename(meetingId, filename), query.data)
        }
      }
      const blob = await archive.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 1 },
      })
      downloadBlob(blob, artifactFilename(meetingId, 'artifacts.zip'))
    },
    onError: () => {
      updateToast('Artifacts could not be downloaded. Please try again.', 'error')
    },
  })
  const accessError = sourceQueries.find(
    (query) => query.isError && isApiError(query.error) && query.error.status === 403
  )
  const allSourcesAbsent = [liveState, refinedState, rawState, chorusState, observationsState].every(
    (state) => state === 'absent'
  )
  const onPlayRange = audioReady ? (startMs: number, endMs: number) => cueRange({ startMs, endMs }, true) : undefined

  const transcriptColumns = useMemo<TranscriptColumn[]>(
    () => [
      {
        id: 'live',
        title: liveLabel,
        state: liveState,
        rows: liveRows,
        onRetry: refetchLive,
      },
      {
        id: 'observations',
        title: 'AX tree',
        state: observationsState,
        rows: observationRows,
        onRetry: refetchObservations,
      },
      ...visibleProviderLanes.map((lane): TranscriptColumn => {
        const rowsByLane = new Map([
          ['raw', rawRows],
          ['chorus', chorusRows],
        ])
        return {
          id: lane.id,
          title: lane.label,
          state: lane.state,
          rows: rowsByLane.get(lane.id) ?? [],
          showText: true,
          onRetry: refetchRaw,
          emptyMessage: lane.emptyMessage,
        }
      }),
      {
        id: 'refined',
        title: refinedLabel,
        state: refinedState,
        rows: refinedRows,
        showText: true,
        onRetry: refetchRefined,
      },
    ],
    [
      liveLabel,
      liveRows,
      liveState,
      observationRows,
      observationsState,
      chorusRows,
      rawRows,
      refetchLive,
      refetchObservations,
      refetchRaw,
      refetchRefined,
      refinedLabel,
      refinedRows,
      refinedState,
      visibleProviderLanes,
    ]
  )

  const audioUrl = audioQuery.data?.url ?? null

  useEffect(() => {
    setAudioReady(false)
    setAudioDurationMs(null)
    setAudioMediaFailed(false)
  }, [audioUrl])

  const retryAudio = () => {
    audioRetryRef.current = 0
    setAudioReady(false)
    setAudioMediaFailed(false)
    void audioQuery.refetch().then((result) => {
      if (!result.data?.url) return
      setAudioLoadAttempt((attempt) => attempt + 1)
    })
  }

  const handleAudioLoadedMetadata = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const seconds = event.currentTarget.duration
    setAudioDurationMs(Number.isFinite(seconds) ? Math.round(seconds * 1000) : null)
    setAudioReady(true)
    setAudioMediaFailed(false)
    audioRetryRef.current = 0
    const pending = pendingClipRef.current
    pendingClipRef.current = null
    if (pending) playFrom(pending.startMs, pending.endMs)
    else if (pinnedTime != null) seekTo(pinnedTime)
  }

  const audioLaneState = !audioRequested
    ? 'idle'
    : audioQuery.isError || audioMediaFailed
      ? 'error'
      : audioQuery.isPending || (audioUrl != null && !audioReady)
        ? 'loading'
        : audioUrl == null
          ? 'absent'
          : 'present'

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (suppressViewerShortcut(event)) return
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault()
        if (!event.repeat) navigateChunk(event.key === 'ArrowLeft' ? -1 : 1)
        return
      }
      if (event.code !== 'Space' && event.key !== ' ') return
      event.preventDefault()
      if (event.repeat) return
      playSelection()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [navigateChunk, playSelection])

  const hasLiveOverlap = useMemo(
    () => liveOverlaps(lanes.find((lane) => lane.id === 'live' && lane.state === 'present')?.spans ?? []).length > 0,
    [lanes]
  )

  const legend = (
    <Legend
      hasLiveOverlap={hasLiveOverlap}
      hasObservations={observationsState === 'present'}
      speakerStyles={speakerStyles}
      speakerMappings={speakerMappings}
      speakerMapState={speakerMapState}
      observationPlatform={observationsArtifact.platform}
      observationOs={observationsArtifact.os}
      onRetrySpeakerMap={() => void speakerMapQuery.refetch()}
      unassignedPeople={unassignedPeople}
    />
  )

  return (
    <div className={clsx(baseStyles.container, styles.container)}>
      <Helmet>
        <title>Attribution Viewer</title>
      </Helmet>

      <div className={styles.content}>
        {accessError && (
          <div className={styles.accessError} role="alert">
            Internal @wispr.ai admin access is required.
          </div>
        )}
        {providersQuery.isError && isApiError(providersQuery.error) && providersQuery.error.status === 409 && (
          <div className={styles.accessError} role="alert">
            The refined transcript changed. Refresh artifacts to compare the current revision.
          </div>
        )}

        {allSourcesAbsent && (
          <div className={styles.emptyMeeting} role="status">
            <h2>No attribution data for this meeting</h2>
            <p>
              No persisted artifacts are available: no live transcript, refined transcript, provider clusters, or AX
              tree observations. The meeting either never produced attribution artifacts or they are no longer retained.
            </p>
          </div>
        )}
        {allSourcesAbsent && legend}

        {!allSourcesAbsent && (
          <>
            <section className={styles.swimlaneCard}>
              <div className={styles.swimlaneToolbar}>
                <div className={styles.swimlaneActions} role="group" aria-label="Transcript artifact actions">
                  <Button
                    title="Refresh artifacts"
                    type="light"
                    onClick={() => void refreshArtifacts()}
                    isLoading={refreshing}
                  />
                  <Button
                    title={
                      <>
                        <Download size={16} aria-hidden="true" />
                        Download transcript artifacts (ZIP)
                      </>
                    }
                    type="light"
                    onClick={() => downloadAllMutation.mutate()}
                    isDisabled={!canDownloadAll}
                    isLoading={downloadAllMutation.isPending}
                  />
                  <Button title="Model comparison" type="light" to={compareHref(meetingId)} />
                </div>
                <div className={styles.timelineControls} role="group" aria-label="Timeline zoom controls">
                  <span className={styles.zoomWindow}>
                    {formatMs(viewStart)} – {formatMs(viewEnd)} {isZoomed ? '(zoomed)' : '(full)'}
                  </span>
                  <Button
                    title={<ZoomOut size={16} aria-hidden="true" />}
                    type="light"
                    className={styles.timelineIconButton}
                    ariaLabel="Zoom out"
                    isDisabled={!isZoomed}
                    width={30}
                    onClick={() => zoomAround(zoomCenter, 2)}
                  />
                  <Button
                    title={<Maximize2 size={16} aria-hidden="true" />}
                    type="light"
                    className={styles.timelineIconButton}
                    ariaLabel="Reset zoom"
                    isDisabled={!isZoomed}
                    width={30}
                    onClick={resetZoom}
                  />
                  <Button
                    title={<ZoomIn size={16} aria-hidden="true" />}
                    type="light"
                    className={styles.timelineIconButton}
                    ariaLabel="Zoom in"
                    isDisabled={viewEnd - viewStart <= minimumView}
                    width={30}
                    onClick={() => zoomAround(zoomCenter, 0.5)}
                  />
                </div>
              </div>
              {isHistoricalJob && (
                <p className={styles.hint} role="status">
                  Only available for the displayed job.
                </p>
              )}
              {isInternalAdmin && (
                <AssignerToggles
                  showFinal={showFinal}
                  onShowFinal={setShowFinal}
                  hasUserLabels={hasUserLabels}
                  showPrior={showPrior}
                  onShowPrior={setShowPrior}
                />
              )}
              {artifactDownloadError && !refreshing && (
                <p className={styles.accessError} role="status">
                  Could not load all transcript artifacts. Click Refresh artifacts to retry the ZIP download.
                </p>
              )}
              <ChunkControls
                chunk={selectedChunk}
                index={chunkIndex}
                count={chunks.length}
                canPrevious={previousChunk >= 0}
                canNext={nextChunk >= 0}
                onNavigate={navigateChunk}
                onPlay={playSelection}
                isPlaying={isPlaying}
                audioUnavailable={audioLaneState === 'absent' || audioLaneState === 'error'}
                playOnSkip={playOnSkip}
                onPlayOnSkip={setPlayOnSkip}
                previewMs={previewMs}
                onPreviewMs={setPreviewMs}
                rate={playbackRate}
                onRate={setPlaybackRate}
              />
              {audioUrl && (
                <audio
                  key={`${audioUrl}:${audioLoadAttempt}`}
                  ref={audioRef}
                  src={audioUrl}
                  preload="metadata"
                  className={styles.audioElement}
                  onLoadedMetadata={handleAudioLoadedMetadata}
                  onTimeUpdate={onTimeUpdate}
                  onSeeked={onSeeked}
                  onPlay={handleAudioPlay}
                  onPause={(event) => {
                    onPause()
                    setPinnedTime(Math.round(event.currentTarget.currentTime * 1000))
                  }}
                  onEnded={onEnded}
                  onError={handleAudioError}
                />
              )}
              <Swimlane
                audioLane={{
                  state: audioLaneState,
                  durationMs: audioDurationMs,
                  isDownloading: audioDownloadMutation.isPending,
                  isPlaying,
                  onDownload: () => audioDownloadMutation.mutate(),
                  onLoad: () => setAudioRequested(true),
                  onRetry: retryAudio,
                  onTogglePlayback: playSelection,
                }}
                lanes={lanes}
                timelineStart={0}
                timelineEnd={endMs}
                viewStart={viewStart}
                viewEnd={viewEnd}
                speakerStyles={speakerStyles}
                hoverTime={hoverTime}
                playheadTime={playheadTime}
                pinnedTime={pinnedTime}
                selectedRange={selectedChunk}
                onNavigateChunk={navigateChunk}
                onHoverTimeChange={setHoverTime}
                onPinnedTimeChange={cueTime}
                onViewChange={setViewport}
                onResetZoom={resetZoom}
              />
            </section>

            <TranscriptColumns
              columns={transcriptColumns}
              activeTime={inspectionTime}
              speakerStyles={speakerStyles}
              zoomed={isZoomed}
              viewStart={viewStart}
              viewEnd={viewEnd}
              onPlayFrom={onPlayRange}
            />

            {legend}

            <StatBand
              endMs={endMs}
              liveRows={liveRows}
              observationCount={observationsArtifact.speaking.length}
              rawRows={rawRows}
              chorusRows={chorusRows}
              refinedRows={refinedRows}
              speakerMappings={speakerMappings}
            />
            <AttributionStages
              diagnostics={attributionDiagnostics}
              isLoading={
                !attributionDiagnostics &&
                (speakerMapQuery.isFetching || refinedQuery.isFetching || refreshingArtifacts)
              }
              hasError={speakerMapQuery.isError || refinedQuery.isError}
            />
          </>
        )}
      </div>
    </div>
  )
}

const JOB_POLL_MS = 30_000

const PRIOR_NOTE = 'As refine shipped it, before speakers were set in the app'
const PRIOR_NOTES: Partial<Record<ShippedAttributionStatus, string>> = {
  summary_pending: `${PRIOR_NOTE} · LLM names pending`,
  summary_missing: `${PRIOR_NOTE} · no LLM names recorded`,
}

const PRIOR_EMPTY: Partial<Record<ShippedAttributionStatus, string>> = {
  missing: 'Not recorded: refined before shipped attribution was saved, or the transcript was edited since.',
  unreadable: "The saved shipped attribution couldn't be read.",
  not_shipped: 'No refined transcript has shipped for this meeting.',
}

const rerunErrorMessage = (error: unknown) => {
  const status = isApiError(error) ? error.status : undefined
  if (status === 404) return (isApiError(error) && (error.detail || error.message)) || 'Not found.'
  if (status === 409) return 'A job is still running for this meeting. Try again once it finishes.'
  if (status === 503)
    return "The job was selected but its start wasn't confirmed. It will start automatically within a few minutes."
  return "Couldn't start the job."
}

// Without a job id these are the transcripts the app serves now, whichever job the viewer has selected.
const JobsPanelWithTranscripts = ({
  meetingId,
  ...props
}: { meetingId: string } & Omit<ComponentProps<typeof JobsPanel>, 'shownTranscript' | 'liveTranscript'>) => {
  const options = { gcTime: 0, retry: shouldRetryArtifact, staleTime: Infinity } as const
  const shownQuery = useQuery({
    ...options,
    ...refinedArtifactQueryOptions(meetingId),
    select: (response) => transcriptStats(entriesToRows(parseTranscriptNdjson(response.data).entries, 'refined')),
  })
  const liveQuery = useQuery({
    ...options,
    ...liveArtifactQueryOptions(meetingId),
    select: (body) => transcriptStats(entriesToRows(parseTranscriptNdjson(body).entries, 'live')),
  })
  return <JobsPanel {...props} shownTranscript={shownQuery.data ?? null} liveTranscript={liveQuery.data ?? null} />
}

const AttributionViewerPage = () => {
  const { meetingId = '' } = useParams<{ meetingId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const mode = parseViewerMode(searchParams.get('mode'))
  const requestedJobId = searchParams.get('job')
  const jobsQuery = useQuery({
    queryKey: attributionViewerKeys.jobs(meetingId),
    queryFn: ({ signal }) => getMeetingAttributionJobs(meetingId, signal).then((response) => response.data),
    enabled: Boolean(meetingId),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // React Query pauses the interval while the tab is hidden.
    refetchInterval: (query) => (query.state.data?.jobs.some((job) => job.active) ? JOB_POLL_MS : false),
    refetchIntervalInBackground: false,
  })
  const { selectedJob, apiJobId } = selectAttributionJob(jobsQuery.data, searchParams.get('job'))
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const queryClient = useQueryClient()
  const rerunMutation = useMutation({
    mutationFn: (sourceJobId: string) =>
      rerunMeetingAttributionJob(meetingId, sourceJobId, crypto.randomUUID()).then((response) => response.data),
    onSettled: () => queryClient.invalidateQueries({ queryKey: attributionViewerKeys.jobs(meetingId) }),
  })

  if (!meetingId) return <Navigate to="/app/attribution" replace />
  if (requestedJobId !== null && jobsQuery.isPending) return null
  if (requestedJobId !== null && jobsQuery.isError) {
    return (
      <div className={styles.page}>
        <div className={styles.accessError} role="alert">
          Couldn&apos;t load jobs for this meeting.
        </div>
      </div>
    )
  }

  const setMode = (next: ViewerMode) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'inspect') params.delete('mode')
    else params.set('mode', next)
    setSearchParams(params, { replace: true })
  }

  const setJob = (jobId: string) => {
    const params = new URLSearchParams(searchParams)
    params.set('job', jobId)
    setSearchParams(params, { replace: true })
  }

  return (
    <div className={styles.page}>
      <ModeBar
        mode={mode}
        onModeChange={setMode}
        jobs={jobsQuery.data?.jobs}
        selectedJobId={mode === 'annotate' ? (jobsQuery.data?.displayed_job_id ?? null) : (selectedJob?.job_id ?? null)}
        displayedJobId={jobsQuery.data?.displayed_job_id ?? null}
        onJobChange={setJob}
      />
      {mode === 'inspect' && jobsQuery.data && jobsQuery.data.jobs.length > 0 && (
        <JobsPanelWithTranscripts
          meetingId={meetingId}
          jobs={jobsQuery.data.jobs}
          currentJobId={jobsQuery.data.current_job_id ?? null}
          canRerun={isInternalAdmin}
          isRefreshing={jobsQuery.isFetching}
          onRefresh={() => void jobsQuery.refetch()}
          isRerunning={rerunMutation.isPending}
          rerunError={rerunMutation.error ? rerunErrorMessage(rerunMutation.error) : null}
          onRerun={(sourceJobId) => rerunMutation.mutateAsync(sourceJobId).catch(() => undefined)}
        />
      )}
      {mode === 'annotate' ? (
        <AnnotateMode key={meetingId} meetingId={meetingId} />
      ) : mode === 'replay' ? (
        <ReplayMode key={meetingId} meetingId={meetingId} />
      ) : (
        <AttributionViewer
          key={meetingId}
          meetingId={meetingId}
          effectiveJobId={apiJobId}
          isHistoricalJob={apiJobId !== null}
        />
      )}
    </div>
  )
}

export default AttributionViewerPage
